import FEIM, { IApiConfig, IErrorMessage } from '@rlx/feim';

import { GameErrorCodes, LicenseType, ToGamePostMessage } from '../../enums';
import { GameErrorPayload, LaunchData } from '../../models';
import { IntegrationBase } from './base';
import { RootStore } from '../../store';

export class RelaxIntegration extends IntegrationBase {

  public gameConfig: IApiConfig = {};

  public needPauseGame = false;

  constructor(rootStore: RootStore) {
    super(rootStore);
    const { data: launchData } = this.rootStore.gameStore.launchData;
    if (launchData) {
      this.init(launchData);
    }
  }

  public init(data: LaunchData): void {
    this.configure(data.currency);
    this.registerEventListeners();
  }

  private configure(currency: string): void {
    FEIM.configure({
      p2pConfig: { currency }
    });
  }

  private registerEventListeners(): void {

    FEIM.on.initialized(() => {
      const { data: launchData } = this.rootStore.gameStore.launchData;
      this.gameConfig = FEIM.getConfiguration();
      this.updateAuthInfo(launchData);
    });

    FEIM.on.refreshBalance(() => {
      this.rootStore.postMessageStore.sendUpdateBalance();
    });

    FEIM.on.updateSettings((settings) => {
      this.rootStore.gameStore.handleSoundEnabledUpdate(!!settings.sounds);
      this.rootStore.postMessageStore.sendUpdateSettings({ sound: settings.sounds, fastPlay: settings.fastPlay });
    });

    FEIM.on.togglePaytable(() => {
      this.rootStore.postMessageStore.sendTogglePayTable();
    });

    FEIM.on.toggleGameHelp(() => {
      this.rootStore.postMessageStore.sendToggleGameHelp();
    });

    FEIM.on.freeze(() => {
      this.puaseGame();
    });

    FEIM.on.unfreeze(() => {
      this.resumeGame();
    });

    FEIM.on.errorMessageDisplayed(() => {
      this.puaseGame();
    });

    FEIM.on.errorMessageDismissed(() => {
      this.resumeGame();
    });

    FEIM.on.pauseAutoPlay(() => {
      this.rootStore.postMessageStore.sendStopAutoSpins();
    });

    window.addEventListener('message', (event) => {

      const { feimOpEvent } = event.data || {};

      if (feimOpEvent) {
        switch (feimOpEvent) {
          case 'pause-when-idle':
            if (!this.rootStore.gameStore.roundInProgress) {
              this.puaseGame();
            } else {
              this.needPauseGame = true;
            }
            break;
          default:
            console.log(`Sorry, we does not support ${feimOpEvent}.`);
        }

      }
    });

  }

  private puaseGame(timeout = 200): void {
    setTimeout(() => {
      this.rootStore.postMessageStore.sendPauseGame();
      this.rootStore.gameStore.setGamePaused(true);
    }, timeout);
  }

  public resumeGame(timeout = 200): void {
    setTimeout(() => {
      this.rootStore.postMessageStore.sendResumeGame();
      this.rootStore.gameStore.setGamePaused(false);
    }, timeout);
  }

  public exitGame(exitUrl = ''): void {
    if (this.gameConfig.operatorHandlesGameExit) {
      FEIM.send.exitGame();
    } else {
      window.location.href = exitUrl;
    }
  }

  public updateAuthInfo(launchData: LaunchData | null) {
    if (!launchData) return;
    const { isAuthenticated, hasJackpot } = launchData;
    const authPayload = {
      name: ToGamePostMessage.AuthInfoUpdate,
      accessToken: this.getAccessToken(launchData),
      needHandleErrors: !this.gameConfig.operatorHandlesErrors,
      isAuthenticated,
      payload: { hasJackpot }
    };
    this.rootStore.postMessageStore.sendMessage(authPayload);
    if (this.rootStore.gameStore.launchData.data?.hasTournament) {
      this.rootStore.postMessageStore.sendTournamentMessage(authPayload);
    }
    if (this.rootStore.gameStore.launchData.data?.lobbyId) {
      this.rootStore.postMessageStore.sendLobbyMessage(authPayload);
    }
    if (this.rootStore.slotHistoryStore.isSlotHistoryOpen) {
      this.rootStore.postMessageStore.sendSlotHistoryMessage(authPayload);
    }
  }

  public handleGameLoaded(launchData: LaunchData | null, stakeAmount = 0) {

    FEIM.send.gameLoadCompleted();

    if (stakeAmount) {
      this.handleStakeUpdate({ stake: stakeAmount });
    }

    if (launchData?.showPayTable) {
      this.rootStore.postMessageStore.sendOpenPayTable();
    }
    if (launchData?.licenseType === LicenseType.BrazilGLA) {
      this.rootStore.postMessageStore.sendIsBrazilGLA();
    }
    if (this.rootStore.sidebarMenuStore.showSidebar) {
      this.rootStore.sidebarMenuStore.sendSidebarPostMessage();
    }
  }

  public handleRoundStarted(payload: any) {
    this.rootStore.gameStore.setRoundInProgress(true);
    FEIM.send.roundStarted({ balance: payload?.balance * 100 });
  }

  public handleSettingsUpdated(payload?: { sound?: boolean, fastPlay?: boolean }) {
    FEIM.send.updateSettings({ sounds: payload?.sound, fastPlay: payload?.fastPlay });
    if (payload?.hasOwnProperty('sound')) {
      this.rootStore.gameStore.handleSoundEnabledUpdate(!!payload?.sound);
    }
    if (!this.rootStore.loaderStore.gameLoaded) {
      FEIM.send.gameLoadStarted();
    }
  }

  public handleBalanceUpdated(payload: any) {
    FEIM.send.balanceUpdate(payload?.balance * 100);
  }

  public handleStakeUpdate(payload?: any) {
    FEIM.send.betUpdate(payload.stake * 100);
    this.rootStore.gameStore.setStakeAmount(payload.stake);
  }

  public handleAutoPlayStarted() {
    FEIM.send.autoPlayStarted();
  }

  public handleAutoPlayFinished() {
    FEIM.send.autoPlayFinished();
  }

  public handleRoundFinished(payload: any) {
    if (this.rootStore.jackpotStore.openJackpotSceneAfterRoundFinished) {
      this.rootStore.jackpotStore.showJackpotScene();
      this.rootStore.jackpotStore.setOpenJackpotSceneAfterRoundFinished(false)
    }
    if (this.rootStore.boostedJackpotStore.openBoostedJackpotAfterRoundFinished) {
      this.rootStore.boostedJackpotStore.showBoostedJackpotWheel();
      this.rootStore.boostedJackpotStore.setOpenBoostedJackpotAfterRoundFinished(false);
    }
    this.rootStore.gameStore.setRoundInProgress(false);
    if (this.needPauseGame) {
      this.puaseGame();
      this.needPauseGame = false;
    }
    FEIM.send.roundFinished({
      bet: Math.trunc(payload?.bet * 100),
      win: { win: Math.trunc(payload?.win * 100) }
    });
  }

  public handleErrorReceived(data: GameErrorPayload) {
    const { errorCode } = data;
    const { operatorHandlesErrors } = this.gameConfig;
    const relaxErrorMessage: { errorCode: null | number, errorMessage: string } = {
      errorCode: null,
      errorMessage: ''
    };

    if (data.operatorErrorCode) {
      relaxErrorMessage.errorCode = data.operatorErrorCode as number;
    }

    switch (errorCode) {

      case GameErrorCodes.GameIsOpen:
        if (!operatorHandlesErrors) {
          this.rootStore.modalStore.openGameIsOpenedModal(data);
        }
        break;
      case GameErrorCodes.ConnectionError:
        if (!operatorHandlesErrors) {
          this.rootStore.modalStore.openLostConnectionModal(data);
        }
        break;
      case GameErrorCodes.Reconnect:
        relaxErrorMessage.errorCode = null;
        break;
      case GameErrorCodes.NotEnoughMoney:
        if (!operatorHandlesErrors) {
          this.rootStore.modalStore.openNotEnoughMoneyModal(data);
        }
        break;
      case GameErrorCodes.PlayerLimitExceeded:
        if (!operatorHandlesErrors) {
          this.rootStore.modalStore.openPlayerLimitExceededModal(data);
        }
        break;
      case GameErrorCodes.GeneralError:
        if (!operatorHandlesErrors) {
          this.rootStore.modalStore.openGeneralErrorModal(data);
        }
        break;
      case GameErrorCodes.SessionExpired:
        if (!operatorHandlesErrors) {
          this.rootStore.modalStore.openSessionTerminatedModal(data);
        }
        break;
      case GameErrorCodes.OperatorError:
        if (!operatorHandlesErrors) {
          this.rootStore.modalStore.openOperatorErrorModal(data);
        }
        break;
    }

    if (relaxErrorMessage.errorCode && operatorHandlesErrors) {
      FEIM.send.errorMessage(relaxErrorMessage as IErrorMessage);
    }

  }

}
